import React, { useEffect, useState } from 'react';
import AppLayout from '../../../components/layout/AppLayout';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { TWorkflow, TWorkflowStep, TWorkflowStepUserType } from '../../../models/workflow/workflow';
import { Button, Input, message, Space, Table, Switch, Spin, Select, Tag } from 'antd';
import { SaveOutlined, EditOutlined, StopOutlined, DeleteOutlined } from '@ant-design/icons';
import { useWorkflowApi } from '../../../api/workflow/workflow';
import type { ColumnsType } from 'antd/es/table';
import ConfirmationDialog from '../../../components/layout/ConfirmationDialog';
import { useReferential } from '../../../context/ReferentialContext';
import { CODIFICATION_TYPE } from '../../../constant/codification';

const WorkflowStepsList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const workflow: TWorkflow | undefined = location.state?.workflow;

  const {
    getWorkflowStepsByWorkflowId,
    createWorkflowStep,
    updateWorkflowStep,
    deleteWorkflowStep,
    getWorkflowStepUserTypesByStepId,
    createWorkflowStepUserType,
    updateWorkflowStepUserType,
    deleteWorkflowStepUserType,
  } = useWorkflowApi();
const { getCodificationsByCodeType } = useReferential();
  const userTypes = getCodificationsByCodeType(CODIFICATION_TYPE.USER_TYPE);
  const [steps, setSteps] = useState<TWorkflowStep[]>([]);
    const [permissions, setPermissions] = useState<Record<number, TWorkflowStepUserType[]>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [addingId, setAddingId] = useState<number>(0);
  const [loadingRow, setLoadingRow] = useState<number | null>(null);
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [editingStep, setEditingStep] = useState<TWorkflowStep | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState<boolean>(false);

    useEffect(() => {
      if (!workflow) {
        message.error('No workflow selected');
        return;
      }
      setLoading(true);
      // Load steps
      getWorkflowStepsByWorkflowId(workflow.id)
        .then((resp) => {
          const data = resp.data || [];
          setSteps(data);
          // For each step, fetch existing permissions
          return Promise.all(
            data.map((step) =>
              getWorkflowStepUserTypesByStepId(step.id).then((p) => ({
                stepId: step.id,
                perms: p.data || [],
              }))
            )
          );
        })
        .then((results) => {
          const permsMap: Record<number, TWorkflowStepUserType[]> = {};
          results.forEach(({ stepId, perms }) => {
            permsMap[stepId] = perms;
          });
          setPermissions(permsMap);
        })
        .catch((err) => message.error(err.response?.data ?? err.message))
        .finally(() => setLoading(false));
      // eslint-disable-next-line
    }, [workflow]);
  
  const onUserTypesChange = (stepId: number, selectedIds: number[]) => {
      const existing = permissions[stepId] || [];
      const toAdd = selectedIds.filter(
        (id) => !existing.some((p) => p.usertype_id === id)
      );
      const toRemove = existing.filter(
        (p) => !selectedIds.includes(p.usertype_id)
      );
  
      toAdd.forEach((uid) => {
        createWorkflowStepUserType({
          id: 0,
          usertype_id: uid,
          workflow_step_id: stepId,
        })
          .then((resp) => {
            setPermissions((prev) => ({
              ...prev,
              [stepId]: [...(prev[stepId] || []), resp.data],
            }));
          })
          .catch((err) => message.error(err.response?.data ?? err.message));
      });
  
      toRemove.forEach((perm) => {
        deleteWorkflowStepUserType(perm.id)
          .then(() => {
            setPermissions((prev) => ({
              ...prev,
              [stepId]: prev[stepId].filter((p) => p.id !== perm.id),
            }));
          })
          .catch((err) => message.error(err.response?.data ?? err.message));
      });
  
      // Update existing ones if needed (flags etc.)
      const toUpdate = existing.filter((p) => selectedIds.includes(p.usertype_id));
      toUpdate.forEach((perm) => {
        updateWorkflowStepUserType(perm.id, perm)
          .catch((err) => message.error(err.response?.data ?? err.message));
      });
      
    };
  
  
  const handleAddStep = () => {
    if (!workflow) return;
    const idRow = addingId - 1;
    const newStep: TWorkflowStep = {
      id: idRow,
      workflow_id: workflow.id,
      code: '',
      name: '',
      description: '',
      step_order: (steps?.length || 0) + 1,
      can_be_skipped: false,
      can_go_back: false,
      next_button: 'Next',
      back_button: 'Back',
    } as TWorkflowStep;

    setAddingId(idRow);
    setEditingKey(idRow);
    setEditingStep(newStep);
    setSteps((prev) => [newStep, ...(prev ?? [])]);
  };

    const edit = (step: TWorkflowStep) => {
      setEditingKey(step.id);
      setEditingStep({ ...step });
    };

  const save = (id: number) => {
    if (!editingStep) return;
    setLoadingRow(id);
    const action = editingStep.id > 0
      ? updateWorkflowStep(editingStep.id, editingStep)
      : createWorkflowStep(editingStep);

    action
      .then((resp) => {
        const saved = resp.data as TWorkflowStep;
        setSteps((prev) => prev.map((s) => (s.id === id ? saved : s)));
        message.success(t('message.savedSuccessfully'));
      })
      .catch((err) => message.error(err.response?.data ?? err.message))
      .finally(() => {
        setLoadingRow(null);
        setEditingKey(null);
        setEditingStep(null);
      });
  };

  const cancel = () => {
    setEditingKey(null);
    setEditingStep(null);
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setIsDeleteDialogVisible(true);
  };

  const handleDelete = () => {
    if (deleteId == null) return;
    setDeleteLoading(true);
    deleteWorkflowStep(deleteId)
      .then(() => {
        setSteps((prev) => prev.filter((s) => s.id !== deleteId));
        message.success(t('message.deletedSuccessfully'));
      })
      .catch((err) => message.error(err.response?.data ?? err.message))
      .finally(() => {
        setDeleteLoading(false);
        setIsDeleteDialogVisible(false);
        setDeleteId(null);
      });
  };

  //const showProjects = (step: TWorkflowStep) => {
  //  navigate('/setup/workflow-projects', { state: { workflow, step } });
  //};

  const columns: ColumnsType<TWorkflowStep> = [
    {
      title: t('workflow.code'),
      dataIndex: 'code',
      key: 'code',
      render: (text, record) =>
        editingKey === record.id ? (
          <Input
            value={editingStep?.code}
            onChange={(e) => editingStep && setEditingStep({ ...editingStep, code: e.target.value })}
          />
        ) : (
          text
        ),
    },
    {
      title: t('workflow.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record) =>
        editingKey === record.id ? (
          <Input
            value={editingStep?.name}
            onChange={(e) => editingStep && setEditingStep({ ...editingStep, name: e.target.value })}
          />
        ) : (
          text
        ),
    },
    {
      title: t('workflow.description'),
      dataIndex: 'description',
      key: 'description',
      render: (text: string = '', record) =>
        editingKey === record.id ? (
          <Input
            value={editingStep?.description}
            onChange={e =>
              editingStep && setEditingStep({ ...editingStep, description: e.target.value })
            }
          />
        ) : (
          text
        ),
    },
    {
      title: t('workflow.order'),
      dataIndex: 'step_order',
      key: 'step_order',
      width: 80,
      render: (text: number, record) =>
        editingKey === record.id ? (
          <Input
            type="number"
            value={editingStep?.step_order}
            onChange={(e) => editingStep && setEditingStep({ ...editingStep, step_order: parseInt(e.target.value) })}
          />
        ) : (
          text
        ),
    },
    {
      title: t('workflow.userTypes'),
      dataIndex: 'usertype_permissions',
      key: 'usertype_permissions',
      render: (_, record) => {
        // if this is the new unsaved row (temp id < 0), render nothing
        if (record.id < 0 && editingKey === record.id) {
          return null;
        }
        // otherwise, existing logic
        return editingKey === record.id ? (
          <Select
            mode="multiple"
            style={{ minWidth: 200 }}
            placeholder={t('workflow.selectUserTypes')}
            options={userTypes.map((u) => ({ label: u.label, value: u.id }))}
            value={permissions[record.id]?.map((p) => p.usertype_id) || []}
            onChange={(vals) => onUserTypesChange(record.id, vals as number[])}
          />
        ) : (
          (permissions[record.id] || []).map((p) => (
            <Tag key={p.usertype_id}>
              {userTypes.find((u) => u.id === p.usertype_id)?.label}
            </Tag>
          ))
        );
      },
    },
    {
      title: t('workflow.nextButton'),
      dataIndex: 'next_button',
      key: 'next_button',
      render: (text: string = '', record) =>
        editingKey === record.id ? (
          <Input
            value={editingStep?.next_button}
            onChange={e =>
              editingStep && setEditingStep({ ...editingStep, next_button: e.target.value })
            }
          />
        ) : (
          text
        ),
    },
    {
      title: t('workflow.backButton'),
      dataIndex: 'back_button',
      key: 'back_button',
      render: (text: string = '', record) =>
        editingKey === record.id ? (
          <Input
            value={editingStep?.back_button}
            onChange={e =>
              editingStep && setEditingStep({ ...editingStep, back_button: e.target.value })
            }
          />
        ) : (
          text
        ),
    },
    {
      title: t('workflow.skip'),
      dataIndex: 'can_be_skipped',
      key: 'skip',
      width: 80,
      render: (val: boolean, record) =>
        editingKey === record.id ? (
          <Switch
            checked={editingStep?.can_be_skipped}
            onChange={(checked) => editingStep && setEditingStep({ ...editingStep, can_be_skipped: checked })}
          />
        ) : (
          <Switch checked={val} disabled />
        ),
    },
    {
      title: t('workflow.back'),
      dataIndex: 'can_go_back',
      key: 'back',
      width: 80,
      render: (val: boolean, record) =>
        editingKey === record.id ? (
          <Switch
            checked={editingStep?.can_go_back}
            onChange={(checked) => editingStep && setEditingStep({ ...editingStep, can_go_back: checked })}
          />
        ) : (
          <Switch checked={val} disabled />
        ),
    },
    {
      title: t('workflow.actions'),
      key: 'actions',
      fixed: 'right' as 'right',
      width: 180,
      render: (_: any, record: TWorkflowStep) => (
        <Space size="middle">
          {loadingRow === record.id ? (
            <Spin />
          ) : editingKey === record.id ? (
            <>
              <Button icon={<SaveOutlined />} onClick={() => save(record.id)} />
              <Button icon={<StopOutlined />} onClick={cancel} />
            </>
          ) : (
            <>
              <Button icon={<EditOutlined />} onClick={() => edit(record)} />
              {/*<Button icon={<EyeOutlined />} onClick={() => showProjects(record)} />*/}
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={() => confirmDelete(record.id)}
                loading={deleteLoading && deleteId === record.id}
              />
            </>
          )}
        </Space>
      ),
    },
  ];

  const handleBackNavigation = () => {
    navigate(-1);
  };

  return (
    <AppLayout 
      title={`Workflow: ${workflow?.name}`} 
      loading={loading}
      onBackNavigation={handleBackNavigation}
      showBackButton={true}
    >
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleAddStep}>
          {t('button.add')}
        </Button>
      </Space>
      <Table<TWorkflowStep>
        columns={columns}
        dataSource={steps}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 'max-content' }}
      />

      <ConfirmationDialog
        visible={isDeleteDialogVisible}
        onConfirm={handleDelete}
        onCancel={() => setIsDeleteDialogVisible(false)}
        loading={deleteLoading}
        title={t('workflow.confirmDeleteStepTitle')}
        description={t('workflow.confirmDeleteStepDescription')}
      />

      
    </AppLayout>
  );
};

export default WorkflowStepsList;
